import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { fetchCalendarMarkers } from 'api/calendarMarkers';

export const MARKER_CONFIG = {
  festivo: { label: 'Festivo', color: '#ff7c80' },
  local: { label: 'Local', color: '#ffd966' },
  puente: { label: 'Puente', color: '#92d050' },
  puente_media: { label: 'Puente Media Plantilla', color: '#c8e5a7' }
};

export const MARKER_KINDS = Object.keys(MARKER_CONFIG);

const CalendarMarkersContext = createContext({
  markers: [],
  markersMap: {},
  loading: false,
  error: null,
  getMarker: () => null,
  reloadMarkers: async () => {}
});

export const useCalendarMarkers = () => {
  const context = useContext(CalendarMarkersContext);
  if (!context) {
    throw new Error('useCalendarMarkers debe usarse dentro de CalendarMarkersProvider');
  }
  return context;
};

const toIsoDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildMarkersMap = (items) => {
  return (items || []).reduce((acc, item) => {
    const iso = toIsoDate(item?.date);
    const kind = (item?.kind || '').toString().trim().toLowerCase();
    if (iso && kind) {
      acc[iso] = kind;
    }
    return acc;
  }, {});
};

export const CalendarMarkersProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [markers, setMarkers] = useState([]);
  const [markersMap, setMarkersMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMarkers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchCalendarMarkers();
      setMarkers(list);
      setMarkersMap(buildMarkersMap(list));
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar los marcadores del calendario.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setMarkers([]);
      setMarkersMap({});
      setError(null);
      setLoading(false);
      return;
    }
    loadMarkers();
  }, [authLoading, user?.id, loadMarkers]);

  const getMarker = useCallback(
    (value) => {
      const iso = toIsoDate(value);
      if (!iso) return null;
      const kind = markersMap[iso];
      if (!kind) return null;
      const config = MARKER_CONFIG[kind];
      if (!config) return { kind, label: kind, color: '#666' };
      return { kind, date: iso, ...config };
    },
    [markersMap]
  );

  const value = useMemo(
    () => ({
      markers,
      markersMap,
      loading,
      error,
      getMarker,
      reloadMarkers: loadMarkers
    }),
    [markers, markersMap, loading, error, getMarker, loadMarkers]
  );

  return <CalendarMarkersContext.Provider value={value}>{children}</CalendarMarkersContext.Provider>;
};
